/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import "./DonationInfoReauthenticate.css";
import { simpleFetch } from "../../restWrapper.js";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import PropTypes from "prop-types";
import { validPaymentInfo } from "../../assets/validators/payment.js";
import { NumericFormat } from "react-number-format";
import { renderErrorMsg } from "../../assets/component/renderErrorMsg.js";
import { Helmet } from "react-helmet";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { resolveThemeColors } from "../../assets/helper";
import { Link } from "react-router-dom";
import paymentNetworksImage from "./images/paymentNetworks.png";

class CustomCalendarInput extends Component {
  render() {
    return (
      <input
        onClick={this.props.onClick}
        onChange={this.props.onChange}
        value={this.props.value}
        className="DonationInfoReauthenticate-dateField"
        type="text"
      />
    );
  }
}

CustomCalendarInput.propTypes = {
  onClick: PropTypes.func,
  onChange: PropTypes.func,
  value: PropTypes.string,
};

// PaymentIntent statuses on the way to a charge once the 3DS challenge is
// done: the page stays behind the loader through them (CP-408)
const SETTLING_STATUSES = ["processing", "requires_confirmation", "requires_capture", "succeeded"];

class DonationInfoReauthenticate extends Component {
  constructor(props) {
    super(props);
    if (!this.props.location.state.paymentId) {
      this.props.history.push("/donationRecordRecurring");
    }

    this.onClickReprocessBtn = this.onClickReprocessBtn.bind(this);
    this.onClickDeleteBtn = this.onClickDeleteBtn.bind(this);
    this.openCardModal = this.openCardModal.bind(this);
    this.closeCardModal = this.closeCardModal.bind(this);
    this.handleUpdateCard = this.handleUpdateCard.bind(this);

    this.state = {
      ...{
        minDate: new Date(),
        startDate: new Date(),
        loading: true,
        errMsgs: [],
        showCardModal: false, // Show modal for card update
        updatingCard: false, // Track card update in progress
        cardComplete: false, // Track if card input is complete
        savedCards: [], // List of saved payment cards
        loadingCards: false, // Track card loading state
        selectedCardOption: 'new', // 'new' or 'saved'
        selectedSavedCard: null, // Selected saved card ID
        payerInfo: {
          payerName: "",
          payerEmail: "",
          payerPhone: "",
        },
      },
      ...this.props.location.state,
    };

    this.handleDateChange = (date) => {
      let data = this.state.data;
      data.scheduledDate = moment(date).toISOString(false);
      this.setState({
        ...this.state,
        ...{
          data: data,
          startDate: date,
        },
      });
    };

    this.handleAmountChange = (e) => {
      let amount = e.target.value;

      let amountSplit = amount.split("$");
      if (!amountSplit[1]) return false;
      amountSplit = amountSplit[1].split(",");

      let a = "";
      for (var i = 0; i < amountSplit.length; i++) {
        a += amountSplit[i];
      }

      const isAmount = validPaymentInfo.validAmount(
        a,
        this.state.minimumPaymentAmount
      );
      const numberAmount = Number(a);
      let data = this.state.data;
      data.amount = numberAmount;
      this.setState({
        data: data,
        validData: isAmount,
      });
    };

    this.handlePayerName = this.setPayerInfoData.bind(this, "payerName");
    this.handlePayerEmail = this.setPayerInfoData.bind(this, "payerEmail");
    this.handlePayerPhone = this.setPayerInfoData.bind(this, "payerPhone");
  }

  setPayerInfoData(name, e) {
    let data = this.state.payerInfo;
    data[name] = e.target.value;
    this.setState({
      payerInfo: data,
    });
  }

  /*
   * A reauthentication completes the payment it replaces, so the donor is
   * sent to that payment's record in Transactions, which has its receipt
   * (CP-406). Replaces this page in history: going back shouldn't reopen a
   * payment that's no longer waiting.
   */
  showCompletedPayment() {
    this.props.history.replace({
      pathname: "/Transactions",
      state: { openPaymentId: this.props.location.state.paymentId },
    });
  }

  formatStripeError(stripeError) {
    const languageObj = this.props.languageObj || {};

    if (!stripeError) {
      return languageObj.errorPaymentDeclined || "Your payment was declined. Please verify your payment method or try a different card.";
    }

    // Extract error code if in format "error_code: message"
    let errorCode = null;
    let originalMessage = stripeError;

    if (stripeError.includes(':')) {
      const parts = stripeError.split(':');
      errorCode = parts[0].trim();
      originalMessage = parts.slice(1).join(':').trim();
    }

    // Map common Stripe error codes to translated messages
    const errorTranslations = {
      'card_declined': languageObj.errorCardDeclined || "Your card was declined.",
      'expired_card': languageObj.errorExpiredCard || "Your card has expired.",
      'incorrect_cvc': languageObj.errorIncorrectCvc || "Your card's security code is incorrect.",
      'incorrect_number': languageObj.errorIncorrectNumber || "Your card number is incorrect.",
      'invalid_expiry_month': languageObj.errorInvalidExpiryMonth || "Your card's expiration month is invalid.",
      'invalid_expiry_year': languageObj.errorInvalidExpiryYear || "Your card's expiration year is invalid.",
      'invalid_number': languageObj.errorInvalidNumber || "Your card number is invalid.",
      'insufficient_funds': languageObj.errorInsufficientFunds || "Your card has insufficient funds.",
      'processing_error': languageObj.errorProcessingError || "An error occurred while processing your card.",
      'rate_limit': languageObj.errorRateLimit || "Too many requests. Please try again later.",
      'authentication_required': languageObj.errorAuthenticationRequired || "Your card requires authentication.",
      'approve_with_id': languageObj.errorApproveWithId || "The payment cannot be authorized.",
      'call_issuer': languageObj.errorCallIssuer || "Please contact your card issuer.",
      'card_not_supported': languageObj.errorCardNotSupported || "Your card does not support this type of purchase.",
      'card_velocity_exceeded': languageObj.errorCardVelocityExceeded || "You have exceeded the balance or credit limit on your card.",
      'currency_not_supported': languageObj.errorCurrencyNotSupported || "Your card does not support the specified currency.",
      'do_not_honor': languageObj.errorDoNotHonor || "Your card was declined.",
      'do_not_try_again': languageObj.errorDoNotTryAgain || "Your card was declined.",
      'duplicate_transaction': languageObj.errorDuplicateTransaction || "A transaction with identical amount and credit card information was submitted very recently.",
      'fraudulent': languageObj.errorFraudulent || "The payment has been declined as Stripe suspects it is fraudulent.",
      'generic_decline': languageObj.errorGenericDecline || "Your card was declined.",
      'invalid_account': languageObj.errorInvalidAccount || "The card, or account the card is connected to, is invalid.",
      'invalid_amount': languageObj.errorInvalidAmount || "The payment amount is invalid.",
      'issuer_not_available': languageObj.errorIssuerNotAvailable || "The card issuer could not be reached.",
      'lost_card': languageObj.errorLostCard || "The payment has been declined because the card is reported lost.",
      'merchant_blacklist': languageObj.errorMerchantBlacklist || "The payment has been declined.",
      'new_account_information_available': languageObj.errorNewAccountInfo || "The card has been declined. Please try again with updated payment information.",
      'no_action_taken': languageObj.errorNoActionTaken || "The card has been declined.",
      'not_permitted': languageObj.errorNotPermitted || "The payment is not permitted.",
      'offline_pin_required': languageObj.errorOfflinePinRequired || "The card was declined as the PIN was not entered.",
      'online_or_offline_pin_required': languageObj.errorPinRequired || "The card was declined as a PIN is required.",
      'pickup_card': languageObj.errorPickupCard || "The card cannot be used to make this payment.",
      'pin_try_exceeded': languageObj.errorPinTryExceeded || "The allowable number of PIN tries has been exceeded.",
      'reenter_transaction': languageObj.errorReenterTransaction || "Please try again.",
      'restricted_card': languageObj.errorRestrictedCard || "The card cannot be used to make this payment.",
      'revocation_of_all_authorizations': languageObj.errorRevocationAll || "The card has been declined.",
      'revocation_of_authorization': languageObj.errorRevocation || "The card has been declined.",
      'security_violation': languageObj.errorSecurityViolation || "The card has been declined.",
      'service_not_allowed': languageObj.errorServiceNotAllowed || "The card has been declined.",
      'stolen_card': languageObj.errorStolenCard || "The payment has been declined because the card is reported stolen.",
      'stop_payment_order': languageObj.errorStopPayment || "The card has been declined.",
      'testmode_decline': languageObj.errorTestmodeDecline || "A test card number was used.",
      'transaction_not_allowed': languageObj.errorTransactionNotAllowed || "The card has been declined.",
      'try_again_later': languageObj.errorTryAgainLater || "The card has been declined. Please try again later.",
      'withdrawal_count_limit_exceeded': languageObj.errorWithdrawalLimit || "You have exceeded the balance or credit limit on your card.",
      'payment_method_not_available': languageObj.errorPaymentMethodNotAvailable || "This payment method is no longer available. Please use a different card."
    };

    // Return translated message if error code exists, otherwise return original message
    if (errorCode && errorTranslations[errorCode]) {
      return errorTranslations[errorCode];
    }

    // Check for known technical error patterns and provide user-friendly messages
    const technicalErrorPatterns = [
      {
        pattern: /PaymentMethod was previously used|may not be used again|attach it to a Customer/i,
        message: languageObj.errorPaymentMethodExpired || "This payment method is no longer valid. Please update your card to retry the payment."
      },
      {
        pattern: /No such payment_method|payment_method.*not found/i,
        message: languageObj.errorPaymentMethodNotFound || "The saved payment method could not be found. Please add a new card."
      },
      {
        pattern: /customer.*not found|No such customer/i,
        message: languageObj.errorCustomerNotFound || "There was an issue with your account. Please contact support."
      }
    ];

    for (const { pattern, message } of technicalErrorPatterns) {
      if (pattern.test(originalMessage)) {
        return message;
      }
    }

    // If the error message looks too technical (contains certain keywords), show generic message
    const technicalKeywords = ['PaymentIntent', 'PaymentMethod', 'Customer attachment', 'connected account', 'stripe', 'api'];
    const isTechnicalError = technicalKeywords.some(keyword =>
      originalMessage.toLowerCase().includes(keyword.toLowerCase())
    );

    if (isTechnicalError) {
      return languageObj.errorPaymentFailed || "The payment could not be processed. Please update your card and try again.";
    }

    // Return the original message (without error code prefix if it had one)
    return originalMessage;
  }

  componentWillUnmount() {
    // Clean up intervals if component unmounts
    if (this.retrievePaymentStatus) {
      clearInterval(this.retrievePaymentStatus);
    }
    if (this.retrieveCardStatus) {
      clearInterval(this.retrieveCardStatus);
    }
  }

  componentDidMount() {
    simpleFetch(
      "/donors/payments/" + this.props.location.state.paymentId,
      "GET",
      {}
    ).then((res) => {
      if (res.status !== 200) {
        this.props.history.push("/Transactions/reauthenticate");
        return;
      }

      let data = res.body;

      // Fetch mission details using the projectId from payment data
      const projectId = data.projectId;
      const organizationId = data.organizationId;

      // Helper function to set state after fetching organization
      const setStateWithOrg = (mission, organization) => {
        let minimumPaymentAmount = organization.minimumPaymentAmount || 0;

        const amount = data.amount;
        data.validData = validPaymentInfo.validAmount(
          !amount ? 0 : amount,
          minimumPaymentAmount
        );

        let originalDate = new Date();
        originalDate.setDate(originalDate.getDate() + 1);

        let startDate = originalDate;
        try {
          if (data.scheduledDate) {
            startDate = new Date(data.scheduledDate);
          }
        } catch (e) {
          // Keep originalDate as startDate
        }

        // Initialize payer info from payment data
        const payerInfo = {
          payerName: data.donorFirstName && data.donorLastName
            ? `${data.donorFirstName} ${data.donorLastName}`
            : "",
          payerEmail: data.donorEmail || "",
          payerPhone: data.phoneNumber || "",
        };

        this.setState({
          loading: false,
          data: data,
          mission: mission,
          organization: organization,
          organizationId: organizationId, // Store organizationId for payments without projectId
          minimumPaymentAmount: minimumPaymentAmount,
          minDate: originalDate,
          startDate: startDate,
          email: data.donorEmail,
          // The payment's own card. The Payment model stores it as
          // stripePaymentId; stripePaymentMethodId is never saved, so Retry
          // without choosing a card had nothing to send (CP-417)
          existingPaymentMethod: data.stripePaymentMethodId || data.stripePaymentId,
          paymentMethod: data.stripePaymentMethodId || data.stripePaymentId,
          fingerprint: data.fingerprint,
          scheduledDate: data.scheduledDate,
          payerInfo: payerInfo,
        });
      };

      // Fetch organization data first (always needed)
      simpleFetch("/donors/organizationView/" + organizationId, "GET", {}).then((orgRes) => {
        const organization = orgRes.body.organizationObj;

        // If projectId exists, fetch mission details
        if (projectId) {
          simpleFetch("/donors/project/" + projectId, "GET", {}).then((missionRes) => {
            const mission = missionRes.body;
            setStateWithOrg(mission, organization);
          }).catch((error) => {
            // console.error("Error fetching mission data:", error);
            // Still continue with null mission - payment might be a quick payment without project
            setStateWithOrg(null, organization);
          });
        } else {
          // No projectId - this is a quick payment without a project
          setStateWithOrg(null, organization);
        }
      }).catch((error) => {
        // console.error("Error fetching organization data:", error);
        this.setState({
          loading: false,
          errMsgs: ["Failed to load organization details. Please try again."],
        });
      });
    });
  }

  openCardModal(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Fetch saved cards when opening modal
    this.setState({
      showCardModal: true,
      errMsgs: [],
      cardComplete: false,
      loadingCards: true,
      selectedCardOption: 'saved', // Default to saved cards
      selectedSavedCard: null
    });

    simpleFetch("/cards", "GET").then((res) => {
      if (res.status === 200 && res.body && res.body.data && res.body.data.length > 0) {
        this.setState({
          savedCards: res.body.data,
          loadingCards: false,
        });
      } else {
        // No saved cards, switch to new card option
        this.setState({
          savedCards: [],
          loadingCards: false,
          selectedCardOption: 'new'
        });
      }
    }).catch((err) => {
      //console.error("Error fetching saved cards:", err);
      this.setState({
        savedCards: [],
        loadingCards: false,
        selectedCardOption: 'new'
      });
    });
  }

  closeCardModal() {
    this.setState({
      showCardModal: false,
      errMsgs: [],
      cardComplete: false,
      selectedCardOption: 'new',
      selectedSavedCard: null
    });
  }

  async handleUpdateCard() {
    this.setState({ updatingCard: true, errMsgs: [] });

    try {
      // Validate payer information
      if (!this.state.payerInfo.payerName || !this.state.payerInfo.payerEmail || !this.state.payerInfo.payerPhone) {
        this.setState({
          errMsgs: ["Please fill in all payer information fields."],
          updatingCard: false,
        });
        return;
      }

      // If using saved card
      if (this.state.selectedCardOption === 'saved') {
        if (!this.state.selectedSavedCard) {
          this.setState({
            errMsgs: ["Please select a saved card."],
            updatingCard: false,
          });
          return;
        }

        const selectedCard = this.state.savedCards.find(c => c.id === this.state.selectedSavedCard);
        if (!selectedCard) {
          this.setState({
            errMsgs: ["Selected card not found."],
            updatingCard: false,
          });
          return;
        }

        // Update state with selected saved card
        this.setState({
          paymentMethod: selectedCard.id,
          fingerprint: selectedCard.card.fingerprint || null,
          data: {
            ...this.state.data,
            cardBrand: selectedCard.card.brand,
            cardLast4: selectedCard.card.last4,
          },
          showCardModal: false,
          updatingCard: false,
          errMsgs: [],
        });

        alert("Payment method updated! Click Reprocess to retry the payment.");
        return;
      }

      // If using new card
      if (!this.props.stripe || !this.props.elements) {
        this.setState({
          errMsgs: ["Payment system not ready. Please refresh the page."],
          updatingCard: false,
        });
        return;
      }

      if (!this.state.cardComplete) {
        this.setState({
          errMsgs: ["Please complete the card information."],
          updatingCard: false,
        });
        return;
      }

      const cardElement = this.props.elements.getElement(CardElement);
      const { error, paymentMethod } = await this.props.stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: this.state.payerInfo.payerName,
          email: this.state.payerInfo.payerEmail,
          phone: this.state.payerInfo.payerPhone,
        },
      });

      if (error) {
        this.setState({
          errMsgs: [error.message || "Failed to process card information."],
          updatingCard: false,
        });
        return;
      }

      // Save the card to the user's account
      const saveCardRes = await simpleFetch("/cards", "POST", {
        paymentMethod: paymentMethod.id,
      });

      if (saveCardRes.status === 412) {
        // Duplicate card - still usable, just show warning
        this.setState({
          paymentMethod: paymentMethod.id,
          fingerprint: paymentMethod.card.fingerprint,
          data: {
            ...this.state.data,
            cardBrand: paymentMethod.card.brand,
            cardLast4: paymentMethod.card.last4,
          },
          showCardModal: false,
          updatingCard: false,
          errMsgs: [],
        });
        alert("This card is already saved. Payment method updated! Click Reprocess to retry the payment.");
        return;
      }

      if (saveCardRes.status !== 200) {
        this.setState({
          errMsgs: [(saveCardRes.body && saveCardRes.body.message) || "Failed to save card. Please try again."],
          updatingCard: false,
        });
        return;
      }

      // Check if 3DS authentication is required
      if (saveCardRes.body && saveCardRes.body.status === 'requires_action') {
        // console.log('3DS authentication required, starting iframe flow...');
        // console.log('Backend response:', saveCardRes.body);

        // Close the modal and show 3DS iframe (following AddCard.js pattern)
        this.setState({
          showCardModal: false,
          updatingCard: false,
          stripe3DS: saveCardRes.body.stripe3DS,
          status: saveCardRes.body.status,
          setupIntent: saveCardRes.body.setupIntent,
          tempPaymentMethod: paymentMethod, // Store for later use
        });

        // console.log('3DS iframe will be displayed, starting status polling...');

        // Start polling for setup intent status (following AddCard.js pattern)
        this.retrieveCardStatus = setInterval(() => {
          simpleFetch("/cards/retrieveCardStatus", "POST", {
            setupIntent: saveCardRes.body.setupIntent,
          }).then((res) => {
            // console.log('Setup intent status:', res.body.status);

            if (res.body.status === "succeeded") {
              clearInterval(this.retrieveCardStatus);
              // console.log('Card setup succeeded!');

              // Update state with the saved card info
              this.setState({
                paymentMethod: this.state.tempPaymentMethod.id,
                fingerprint: this.state.tempPaymentMethod.card.fingerprint,
                data: {
                  ...this.state.data,
                  cardBrand: this.state.tempPaymentMethod.card.brand,
                  cardLast4: this.state.tempPaymentMethod.card.last4,
                },
                stripe3DS: null,
                status: null,
                setupIntent: null,
                tempPaymentMethod: null,
                errMsgs: [],
              });

              alert("Card saved successfully! Click Reprocess to retry the payment.");
            } else if (res.body.status === "requires_payment_method") {
              clearInterval(this.retrieveCardStatus);
              // console.error('Card authentication failed');
              this.setState({
                errMsgs: ["Card authentication failed. Please try a different card."],
                stripe3DS: null,
                status: null,
                setupIntent: null,
                tempPaymentMethod: null,
              });
            }
          }).catch((error) => {
            // console.error('Error polling card status:', error);
            clearInterval(this.retrieveCardStatus);
            this.setState({
              errMsgs: ["Error verifying card. Please try again."],
              stripe3DS: null,
              status: null,
            });
          });
        }, 2000);

        return;
      }

      // Card saved successfully without 3DS
      this.setState({
        paymentMethod: paymentMethod.id,
        fingerprint: paymentMethod.card.fingerprint,
        data: {
          ...this.state.data,
          cardBrand: paymentMethod.card.brand,
          cardLast4: paymentMethod.card.last4,
        },
        showCardModal: false,
        updatingCard: false,
        errMsgs: [],
      });

      alert("Card saved successfully! Click Reprocess to retry the payment.");
    } catch (error) {
      // console.error("Error updating card:", error);
      this.setState({
        errMsgs: ["Failed to update card. Please try again."],
        updatingCard: false,
      });
    }
  }

  onClickReprocessBtn() {
    // Validate that we have all required data
    // Either mission or organizationId is required, plus organization and paymentMethod
    if ((!this.state.mission && !this.state.organizationId) || !this.state.organization || !this.state.paymentMethod) {
      this.setState({
        errMsgs: ["Missing required payment information. Please try again."],
      });
      return;
    }

    this.processPayment();
  }

  processPayment() {
    this.setState({
      loading: true,
      errMsgs: [],
    });

    // Use current date/time for reprocessing (backend validation requires future date)
    const now = new Date();
    const scheduledDate = moment(now).toISOString();

    let paymentInfo = {
      /* Payee provided specifics */
      lastName: this.state.data.donorLastName,
      firstName: this.state.data.donorFirstName,
      email: this.state.data.donorEmail,
      phoneNumber: this.state.data.phoneNumber,
      amount: this.state.data.amount,
      memo: this.state.data.memo,
      country: this.state.data.country || "HK",
      scheduledDate: scheduledDate, // Use current time for immediate reprocessing

      /* Stripe specific */
      currency: "hkd",
      paymentMethod: this.state.paymentMethod,
      fingerprint: this.state.fingerprint,

      /* Old payment to update */
      oldPaymentId: this.props.location.state.paymentId,
    };

    // Add projectId if mission exists, otherwise use organizationId
    if (this.state.mission && this.state.mission.id) {
      paymentInfo.projectId = this.state.mission.id;
    } else if (this.state.organizationId) {
      paymentInfo.organizationId = this.state.organizationId;
      paymentInfo.projectName = this.state.data.projectName || "Payment";
      paymentInfo.transactionType = this.state.data.type || "donation";
    }

    // Only add recurring fields if this is a recurring payment
    if (this.state.data.isRecurring) {
      paymentInfo.isRecurring = true;
      if (this.state.data.recurringInterval) {
        paymentInfo.recurringInterval = this.state.data.recurringInterval;
      }
      if (this.state.data.recurringDay) {
        paymentInfo.recurringDay = this.state.data.recurringDay;
      }
      if (this.state.data.recurringEndDate) {
        paymentInfo.recurringEndDate = this.state.data.recurringEndDate;
      }
      if (this.state.data.parentPaymentId) {
        paymentInfo.parentPaymentId = this.state.data.parentPaymentId;
      }
    }

    simpleFetch("/donors/payments", "POST", paymentInfo).then((res) => {
      if (!res.body) {
        this.setState({
          loading: false,
          errMsgs: ["Payment processing failed. Please try again."],
        });
        return;
      }

      this.setState({
        loading: false,
        projectId: paymentInfo.projectId,
      });

      if (res.body.status === "succeeded") {
        this.showCompletedPayment();
      } else if (res.body.status === "requires_action") {
        this.setState({
          loading: false,
          stripe3DS: res.body.stripe3DS,
          status: res.body.status,
          paymentIntent: res.body.paymentIntent,
        });

        // One status check at a time and one confirmation per intent:
        // overlapping checks could each see "succeeded" and confirm (CP-406)
        let checkInFlight = false;
        let confirmSent = false;

        /* fetch Stripe payment status every two seconds */
        this.retrievePaymentStatus = setInterval(() => {
          if (checkInFlight || confirmSent) return;
          checkInFlight = true;

          const statusPayload = {
            paymentIntent: this.state.paymentIntent,
          };
          // Add projectId or organizationId based on what we have
          if (paymentInfo.projectId) {
            statusPayload.projectId = paymentInfo.projectId;
          } else if (paymentInfo.organizationId) {
            statusPayload.organizationId = paymentInfo.organizationId;
          }

          simpleFetch("/donors/payments/retrievePaymentStatus", "POST", statusPayload).then((res) => {
            checkInFlight = false;
            // A failed check has no status: keep the challenge up and try
            // again on the next tick (CP-408)
            if (!res || res.status !== 200 || !res.body) return;
            this.setState({
              status: res.body.status,
              // Past the challenge but not settled (e.g. "processing"):
              // cover the page until it is (CP-408)
              loading: SETTLING_STATUSES.includes(res.body.status),
            });
            if (res.body.status === "succeeded") {
              clearInterval(this.retrievePaymentStatus);
              confirmSent = true;
              this.setState({
                loading: true,
                stripe3DS: null, // Hide 3DS iframe
                status: null,
              });
              simpleFetch("/donors/payments/confirm3DSPayment", "POST", {
                ...paymentInfo,
                paymentIntent: this.state.paymentIntent,
                oldPaymentId: this.props.location.state.paymentId, // Pass old payment ID to cancel it
              }).then((confirmRes) => {
                // Check if confirm3DSPayment was successful: only a 200 means
                // the payment was recorded (a 409 is held or needs checking)
                if (!confirmRes || !confirmRes.body || confirmRes.status !== 200) {
                  // console.error("Failed to confirm 3DS payment");
                  this.setState({
                    loading: false,
                    errMsgs: [
                      (confirmRes && typeof confirmRes.body === "object" && confirmRes.body.message) ||
                        "Payment confirmation failed. Please try again.",
                    ],
                    stripe3DS: null,
                    status: null,
                  });
                  return;
                }

                this.showCompletedPayment();
              }).catch((error) => {
                // console.error("Error confirming 3DS payment:", error);
                this.setState({
                  loading: false,
                  errMsgs: ["Payment confirmation failed. Please try again."],
                  stripe3DS: null,
                  status: null,
                });
              });
            } else if (res.body.status === "requires_payment_method") {
              clearInterval(this.retrievePaymentStatus);
              this.setState({
                loading: false,
                errMsgs: ["Payment failed. The payment method was declined."],
              });
            }
          }).catch(() => {
            // A failed check: the next tick tries again
            checkInFlight = false;
          });
        }, 2000);
      } else {
        // Handle other statuses (failed, etc.)
        this.setState({
          loading: false,
          errMsgs: [res.body.message || "Payment processing failed. Please try again."],
        });
      }
    }).catch((error) => {
      // console.error("Payment processing error:", error);
      this.setState({
        loading: false,
        errMsgs: ["An error occurred while processing your payment. Please try again."],
      });
    });
  }


  onClickDeleteBtn() {
    const languageObj = this.props.languageObj;
    var r = window.confirm(languageObj.deleteConfirm || "Delete this Payment?");
    if (r === true) {
      this.setState({
        loading: true,
        errMsgs: [],
      });
      simpleFetch(
        "/donors/payments/" + this.props.location.state.paymentId,
        "DELETE",
        {}
      ).then((res) => {
        // 200 OK or 204 No Content both indicate success
        if (res.status !== 200 && res.status !== 204) {
          this.setState({
            loading: false,
            errMsgs: [(res.body && res.body.message) || languageObj.deleteError || "Failed to delete payment. Please try again."],
          });
          return;
        }
        this.setState({
          deleted: true,
          loading: false,
        });
        setTimeout(
          function () {
            this.props.history.push("/Transactions/reauthenticate");
          }.bind(this),
          1000
        );
      }).catch((error) => {
        // console.error("Error deleting payment:", error);
        this.setState({
          loading: false,
          errMsgs: [languageObj.deleteError || "Failed to delete payment. Please try again."],
        });
      });
    }
  }

  renderRecord(data) {
    const rDate = new Date(data.scheduledDate);
    const d = rDate.getDate();
    const m = rDate.getMonth() + 1;
    const y = rDate.getFullYear();
    const date = y + "/" + m + "/" + d;

    const resolved = resolveThemeColors(this.state.headerColor, this.state.buttonColor);
    const color = {
      headerColor: resolved.headerColor,
      buttonColor: resolved.buttonColor,
    };

    const headerStyle = {
      background: "#" + this.state.headerColor,
    };

    const languageObj = this.props.languageObj;

    return (
      <div className="DonationInfoReauthenticate-view">
        {this.render3DS()}
        <Helmet>
          <title>{this.state.orgName}</title>
        </Helmet>
        <div className="DonationInfoReauthenticate-bg" style={headerStyle} />
        <div className="DonationInfoReauthenticate-container">
          <div className="DonationInfoReauthenticate-header">
            <Link
              className="DonationInfoReauthenticate-backContainer"
              to={{
                pathname: "/Transactions/reauthenticate",
                state: {
                  color: color,
                },
              }}
            >
              <div className="DonationInfoReauthenticate-back">
                <div className="DonationInfoReauthenticate-backBtn"></div>
                <div className="DonationInfoReauthenticate-backText">
                  {languageObj.backTxt}
                </div>
              </div>
            </Link>

            <div
              className="DonationInfoReauthenticate-reprocessContainer"
              onClick={this.onClickReprocessBtn}
            >
              <div className="DonationInfoReauthenticate-reprocessBtn">
                {languageObj.retryPayment || "Retry Payment"}
              </div>
            </div>
          </div>

          <div className="DonationInfoReauthenticate-main">
            <div className="DonationInfoReauthenticate-inputContainer">
              <div className="DonationInfoReauthenticate-heading">
                {this.state.orgName}
              </div>

              {/* Alert Banner */}
              <div style={{
                background: '#FEF3C7',
                border: '1px solid #FCD34D',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '20px',
                marginLeft: '20px',
                marginRight: '20px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px'
              }}>
                <span role="img" aria-label="warning" style={{ fontSize: '24px' }}>⚠️</span>
                <div>
                  <div style={{ fontWeight: '600', fontSize: '15px', color: '#78350F', marginBottom: '4px' }}>
                    {languageObj.paymentDeclined || "Payment Declined"}
                  </div>
                  <div style={{ fontSize: '14px', color: '#78350F', lineHeight: '1.5' }}>
                    {this.formatStripeError(data.stripeError)}
                  </div>
                </div>
              </div>

              {/* Error messages */}
              {this.state.errMsgs.length > 0 && (
                <div style={{
                  background: '#FEF2F2',
                  border: '1px solid #FCA5A5',
                  borderRadius: '12px',
                  padding: '12px 16px',
                  marginBottom: '20px',
                  marginLeft: '20px',
                  marginRight: '20px'
                }}>
                  {this.state.errMsgs.map((err, index) => (
                    <div key={index} style={{ color: '#DC2626', fontSize: '14px' }}>
                      {renderErrorMsg(err)}
                    </div>
                  ))}
                </div>
              )}

              {/* Payment Information Card */}
              <div style={{
                borderRadius: '16px',
                padding: '24px',
                background: 'white',
                marginBottom: '16px'
              }}>
                {/* Amount and Date - 2 columns */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '16px',
                  marginBottom: '16px'
                }}>
                  {/* Amount */}
                  <div style={{
                    border: '1px solid #E5E7EB',
                    borderRadius: '12px',
                    padding: '16px'
                  }}>
                    <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>
                      {languageObj.amount}
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: '600' }}>
                      HK
                      <NumericFormat
                        decimalSeparator="."
                        displayType="text"
                        valueIsNumericString={true}
                        value={data.amount}
                        allowNegative={false}
                        thousandSeparator={true}
                        prefix={"$"}
                        fixedDecimalScale={true}
                        decimalScale={2}
                      />
                    </div>
                  </div>

                  {/* Scheduled Date */}
                  <div style={{
                    border: '1px solid #E5E7EB',
                    borderRadius: '12px',
                    padding: '16px'
                  }}>
                    <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>
                      {languageObj.failedOn || "Last charged on"}
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span role="img" aria-label="calendar">📅</span>
                      <span>{date}</span>
                    </div>
                  </div>
                </div>

                {/* Payment Method */}
                <div style={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>
                    {languageObj.paymentMethod || "Payment method"}
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{ fontSize: '18px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span role="img" aria-label="credit card">💳</span>
                      <span>{data.cardBrand} •••• {data.cardLast4}</span>
                    </div>
                    <button
                      onClick={this.openCardModal}
                      style={{
                        fontSize: '14px',
                        color: '#667eea',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '8px 12px',
                        fontWeight: '500'
                      }}
                    >
                      {languageObj.changeCard || "Change card"}
                    </button>
                  </div>
                </div>

                {/* Project / Item */}
                <div style={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '12px',
                  padding: '16px'
                }}>
                  <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>
                    {languageObj.item || "Item"}
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: '500' }}>
                    {data.projectName}
                  </div>
                </div>
              </div>

              {/* Donor Information */}
              <div style={{
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
                padding: '16px',
                marginTop: '16px',
                marginLeft: '20px',
                marginRight: '20px'
              }}>
                <div style={{
                  fontSize: '14px',
                  color: '#6B7280',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px'
                }}>
                  <span role="img" aria-label="user">👤</span>
                  <span>{languageObj.donorInformation || "Payer Information"}</span>
                </div>
                <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                  <div><strong>{languageObj.nameLabel || "Name:"}</strong> {data.donorFirstName} {data.donorLastName}</div>
                  <div><strong>{languageObj.emailLabel || "Email:"}</strong> {data.donorEmail}</div>
                  <div><strong>{languageObj.phoneLabel || "Phone:"}</strong> {data.phoneNumber}</div>
                </div>
              </div>

              {/* Memo/Note Section */}
              {data.memo && (
                <div style={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '12px',
                  padding: '16px',
                  marginTop: '16px',
                  marginLeft: '20px',
                  marginRight: '20px'
                }}>
                  <div style={{
                    fontSize: '14px',
                    color: '#6B7280',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span role="img" aria-label="note">💬</span>
                    <span>{languageObj.memo || "Note"}</span>
                  </div>
                  <div style={{
                    marginTop: '8px',
                    fontSize: '14px',
                    color: '#1F2937',
                    lineHeight: '1.5',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {data.memo}
                  </div>
                </div>
              )}

              {/* Delete Payment Button */}
              <div style={{
                marginTop: '24px',
                marginLeft: '20px',
                marginRight: '20px',
                textAlign: 'center'
              }}>
                <button
                  onClick={this.onClickDeleteBtn}
                  style={{
                    padding: '12px 24px',
                    background: '#FEF2F2',
                    border: '2px solid #EF4444',
                    borderRadius: '8px',
                    color: '#EF4444',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {languageObj.deleteBtn || "Delete Payment"}
                </button>
              </div>
            </div>
            <div className="DonationInfoReauthenticate-chin" />
          </div>
        </div>

        {/* Card Update Modal */}
        {this.state.showCardModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
            onClick={this.closeCardModal}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                maxWidth: '500px',
                width: '90%',
                maxHeight: '90vh',
                overflow: 'auto',
                position: 'relative'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Payment Networks Logo */}
              <div style={{
                backgroundImage: `url(${paymentNetworksImage})`,
                backgroundPosition: 'right',
                backgroundSize: '80%',
                backgroundRepeat: 'no-repeat',
                width: '220px',
                height: '46px',
                position: 'absolute',
                right: '24px',
                top: '24px',
                pointerEvents: 'none'
              }} />

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                  <span role="img" aria-label="credit card">💳</span> Update Payment Method
                </h2>
                <button
                  onClick={this.closeCardModal}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: '0',
                    lineHeight: '1',
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  ×
                </button>
              </div>

              {this.state.errMsgs.length > 0 && (
                <div style={{
                  background: '#FEF2F2',
                  border: '1px solid #FCA5A5',
                  borderRadius: '8px',
                  padding: '10px',
                  marginBottom: '16px'
                }}>
                  {this.state.errMsgs.map((err, index) => (
                    <div key={index} style={{ color: '#DC2626', fontSize: '14px' }}>
                      {err}
                    </div>
                  ))}
                </div>
              )}

              {/* Loading saved cards indicator */}
              {this.state.loadingCards && (
                <div style={{
                  textAlign: 'center',
                  padding: '20px',
                  fontSize: '14px',
                  color: '#6B7280'
                }}>
                  Loading saved cards...
                </div>
              )}

              {/* Payer Information Fields */}
              {!this.state.loadingCards && (
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>
                      {languageObj.cardholderName || "Cardholder Name"}
                    </div>
                    <input
                      type="text"
                      value={this.state.payerInfo.payerName}
                      onChange={this.handlePayerName}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        fontSize: '16px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>
                      {languageObj.emailAddress || "Email Address"}
                    </div>
                    <input
                      type="email"
                      value={this.state.payerInfo.payerEmail}
                      onChange={this.handlePayerEmail}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        fontSize: '16px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>
                      {languageObj.phone || "Phone"}
                    </div>
                    <input
                      type="tel"
                      value={this.state.payerInfo.payerPhone}
                      onChange={this.handlePayerPhone}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        fontSize: '16px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Card selection options */}
              {!this.state.loadingCards && (
                <>
                  {/* Radio buttons to choose between saved and new card */}
                  {this.state.savedCards.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '12px', fontWeight: '500' }}>
                        Choose payment method:
                      </div>

                      {/* Saved card option */}
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px',
                        border: this.state.selectedCardOption === 'saved' ? '2px solid #667eea' : '1px solid #E5E7EB',
                        borderRadius: '8px',
                        marginBottom: '8px',
                        cursor: 'pointer',
                        background: this.state.selectedCardOption === 'saved' ? '#F5F7FF' : 'white'
                      }}>
                        <input
                          type="radio"
                          name="cardOption"
                          value="saved"
                          checked={this.state.selectedCardOption === 'saved'}
                          onChange={(e) => this.setState({
                            selectedCardOption: e.target.value,
                            errMsgs: []
                          })}
                          style={{ marginRight: '10px', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '14px', fontWeight: '500' }}>Use a saved card</span>
                      </label>

                      {/* New card option */}
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px',
                        border: this.state.selectedCardOption === 'new' ? '2px solid #667eea' : '1px solid #E5E7EB',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        background: this.state.selectedCardOption === 'new' ? '#F5F7FF' : 'white'
                      }}>
                        <input
                          type="radio"
                          name="cardOption"
                          value="new"
                          checked={this.state.selectedCardOption === 'new'}
                          onChange={(e) => this.setState({
                            selectedCardOption: e.target.value,
                            errMsgs: []
                          })}
                          style={{ marginRight: '10px', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '14px', fontWeight: '500' }}>Enter a new card</span>
                      </label>
                    </div>
                  )}

                  {/* Show saved cards list when "saved" option is selected */}
                  {this.state.selectedCardOption === 'saved' && this.state.savedCards.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '12px' }}>
                        Select a saved card:
                      </div>
                      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {this.state.savedCards.map((card) => (
                          <label
                            key={card.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '16px',
                              border: this.state.selectedSavedCard === card.id ? '2px solid #667eea' : '1px solid #E5E7EB',
                              borderRadius: '8px',
                              marginBottom: '8px',
                              cursor: 'pointer',
                              background: this.state.selectedSavedCard === card.id ? '#F5F7FF' : 'white',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                              <input
                                type="radio"
                                name="savedCard"
                                value={card.id}
                                checked={this.state.selectedSavedCard === card.id}
                                onChange={(e) => this.setState({
                                  selectedSavedCard: e.target.value,
                                  errMsgs: []
                                })}
                                style={{ marginRight: '12px', cursor: 'pointer' }}
                              />
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span role="img" aria-label="credit card" style={{ fontSize: '24px' }}>💳</span>
                                <div>
                                  <div style={{ fontSize: '16px', fontWeight: '500', textTransform: 'capitalize' }}>
                                    {card.card.brand} •••• {card.card.last4}
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#6B7280' }}>
                                    Expires {card.card.exp_month}/{card.card.exp_year}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Show CardElement when "new" option is selected or no saved cards */}
                  {(this.state.selectedCardOption === 'new' || this.state.savedCards.length === 0) && (
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '12px' }}>
                        {languageObj.subheading && languageObj.subheading[0] || "Card Information"}
                      </div>
                      <div
                        style={{
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px',
                          padding: '12px'
                        }}
                      >
                        <CardElement
                          options={{
                            style: {
                              base: {
                                fontSize: '16px',
                                color: '#374151',
                                '::placeholder': {
                                  color: '#9CA3AF'
                                }
                              },
                              invalid: {
                                color: '#EF4444'
                              }
                            }
                          }}
                          onChange={(e) => {
                            this.setState({
                              cardComplete: e.complete,
                              errMsgs: e.error ? [e.error.message] : [],
                            });
                          }}
                        />
                      </div>

                      {/* Lock/Security Indicator */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginTop: '16px',
                        marginBottom: '12px'
                      }}>
                        <div style={{
                          width: '24px',
                          height: '24px',
                          marginRight: '8px',
                          display: 'inline-block'
                        }}>
                          <svg viewBox="0 0 24 24" fill="#c4c4c4">
                            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm9 14H6V10h12v10zm-6-3c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"/>
                          </svg>
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: '#c4c4c4'
                        }}>
                          {languageObj.lock || "Secure payment"}
                        </div>
                      </div>

                      {/* Stripe Branding */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginTop: '12px'
                      }}>
                        <div style={{
                          width: '51px',
                          height: '30px',
                          marginRight: '10px',
                          display: 'inline-block'
                        }}>
                          <svg viewBox="0 0 60 25" fill="none">
                            <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.53-.24 1.53-1C6.26 13.77 0 14.51 0 9.95 0 7.04 2.28 5.3 5.62 5.3c1.36 0 2.72.2 4.09.75v3.88a9.23 9.23 0 0 0-4.1-1.06c-.86 0-1.44.25-1.44.93 0 1.85 6.29.97 6.29 5.88z" fill="#6772e5"/>
                          </svg>
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: '#c4c4c4',
                          lineHeight: '1.3'
                        }}>
                          {languageObj.stripe && languageObj.stripe[0] || "Powered by Stripe"}
                          {languageObj.stripe && languageObj.stripe[1] && (
                            <>
                              <br />
                              {languageObj.stripe[1]}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}


              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={this.closeCardModal}
                  disabled={this.state.updatingCard}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    background: 'white',
                    cursor: this.state.updatingCard ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    opacity: this.state.updatingCard ? 0.5 : 1
                  }}
                >
                  {languageObj.cancelBtn || "Cancel"}
                </button>
                <button
                  onClick={this.handleUpdateCard}
                  disabled={
                    this.state.updatingCard ||
                    (this.state.selectedCardOption === 'saved' && !this.state.selectedSavedCard) ||
                    (this.state.selectedCardOption === 'new' && !this.state.cardComplete)
                  }
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '8px',
                    background: '#000',
                    color: 'white',
                    cursor: (
                      this.state.updatingCard ||
                      (this.state.selectedCardOption === 'saved' && !this.state.selectedSavedCard) ||
                      (this.state.selectedCardOption === 'new' && !this.state.cardComplete)
                    ) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    opacity: (
                      this.state.updatingCard ||
                      (this.state.selectedCardOption === 'saved' && !this.state.selectedSavedCard) ||
                      (this.state.selectedCardOption === 'new' && !this.state.cardComplete)
                    ) ? 0.7 : 1
                  }}
                >
                  {this.state.updatingCard
                    ? (languageObj.updating || "Updating...")
                    : (languageObj.updateCard || "Update Card")
                  }
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );

  }
  render3DS() {
    if (this.state.status === "requires_action" && this.state.stripe3DS) {
      return (
        <div className="DonationInfoReauthenticate-iframeContainer">
          <iframe
            className="DonationInfoReauthenticate-iframe"
            src={this.state.stripe3DS}
            title="3D Secure"
            width="100%"
            height="100%"
          />
        </div>
      );
    }
    return null;
  }

  render() {
    if (this.state.loading) {
      return <this.props.RenderFullPageLoading />;
    }

    const data = this.state.data;
    if (this.state.deleted) return <div>deleted</div>;
    else if (!data)
      return (
        <div className="DonationInfoReauthenticate-emptyPage">
          <div className="DonationInfoReauthenticate-center">
            <div className="DonationInfoReauthenticate-btm-load" />
          </div>
        </div>
      );
    else return this.renderRecord(data);
  }
}
DonationInfoReauthenticate.propTypes = {
  languageObj: PropTypes.object,
  stripe: PropTypes.object,
  elements: PropTypes.object,
};

// Wrapper component that provides Stripe context
function DonationInfoReauthenticateWithStripe(props) {
  const stripe = useStripe();
  const elements = useElements();

  return <DonationInfoReauthenticate {...props} stripe={stripe} elements={elements} />;
}

export default DonationInfoReauthenticateWithStripe;
